import React from 'react'
import { Formik, FormikHelpers } from 'formik'
import * as yup from 'yup'
import { MutationTuple } from 'react-apollo'

import AvatarUploadContainer from './AvatarUploadContainer'
import Form from 'components/Form'
import Input, { DecoratedInput } from 'components/Input'
import { ChangeEmailModal } from 'modules/user/modals/ChangeEmailModal'
import { ChangePasswordModal } from 'modules/user/modals/ChangePasswordModal'
import {
  FieldRow,
  NameField,
  NameFieldCaption,
  EditEmailButton,
  SaveNameButton,
  EditPasswordButton,
  EmailField,
  PasswordField,
  NewEmailCaption,
  ResendLink,
  PendingEmailPill,
} from './SettingsForm.styles'

import { useModalContext } from 'containers/ModalManager'
import { useToast } from 'containers/ToastManager'

import {
  useEmailResendChangeConfirmationMutation,
  UserUpdateMutation,
  UserUpdateMutationVariables,
} from 'gqlTypes'

const settingsSchema = yup.object().shape({
  name: yup.string().trim().required('Name can’t be blank'),
})

export interface SettingsFormProps {
  userName: string
  userEmail: string
  userNewEmail: string | null
  userHasAvatar: boolean
  userAvatarSrc: string
  userHasPersonalIdentity: boolean
  userHasPersonalAuthorization: boolean
  userHasSsoWorkspace: boolean
  onUserUpdate: MutationTuple<
    UserUpdateMutation,
    UserUpdateMutationVariables
  >[0]
}

const SettingsForm: React.FC<SettingsFormProps> = props => {
  const {
    userName,
    userEmail,
    userNewEmail,
    userHasAvatar,
    userAvatarSrc,
    userHasPersonalAuthorization,
    userHasSsoWorkspace,
    onUserUpdate,
  } = props

  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const [
    resendConfirmation,
    { loading: isResendingConfirmation },
  ] = useEmailResendChangeConfirmationMutation({
    redirectErrors: false,
    onError: 'unsafe-throw-exception',
  })

  const initialValues = {
    name: userName,
  }

  type Values = typeof initialValues

  const handleOnSubmit = async (
    values: Values,
    actions: FormikHelpers<Values>
  ) => {
    try {
      await onUserUpdate({ variables: { input: values } })
    } catch (e) {
      // Error handling will be done by the parent component
    } finally {
      actions.setSubmitting(false)
    }
  }

  const handleResendConfirmation = async () => {
    if (isResendingConfirmation || !userNewEmail) {
      return
    }

    try {
      const { data } = await resendConfirmation()

      const result = data && data.emailResendChangeConfirmation

      if (result && result.errors && result.errors.length > 0) {
        throw result.errors[0]
      }

      showToast(
        `Please confirm your new email address using the link we sent to '${userNewEmail}'`,
        'positive'
      )
    } catch (error) {
      showToast(error.message, 'negative')
    }
  }

  return (
    <Formik
      validationSchema={settingsSchema}
      initialValues={initialValues}
      onSubmit={handleOnSubmit}>
      {formik => {
        const {
          handleChange,
          handleBlur,
          values,
          errors,
          isSubmitting,
        } = formik

        return (
          <Form style={{ display: 'block' }}>
            <FieldRow>
              <AvatarUploadContainer
                userName={userName}
                userHasAvatar={userHasAvatar}
                userAvatarSrc={userAvatarSrc}
              />

              <NameField name="name" label="Name" errorText={errors.name}>
                <Input
                  name="name"
                  type="text"
                  placeholder="John Appleseed"
                  value={values.name}
                  disabled={!userHasPersonalAuthorization}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </NameField>

              <SaveNameButton
                type="submit"
                variant="secondary"
                loading={isSubmitting}
                disabled={isSubmitting || values.name === userName}>
                Save
              </SaveNameButton>
            </FieldRow>

            {userHasSsoWorkspace && (
              <NameFieldCaption>
                In SSO Workspaces, your name is set by the identity provider
              </NameFieldCaption>
            )}

            {userHasPersonalAuthorization && (
              <>
                <FieldRow>
                  <EmailField name="email" label="Email">
                    <DecoratedInput
                      value={userNewEmail ?? userEmail}
                      label="Email"
                      suffixComponent={
                        userNewEmail && (
                          <PendingEmailPill>Pending</PendingEmailPill>
                        )
                      }
                    />
                  </EmailField>

                  <EditEmailButton
                    onClick={() =>
                      showModal(ChangeEmailModal, {
                        initialEmail: userNewEmail ?? userEmail,
                      })
                    }>
                    Edit&hellip;
                  </EditEmailButton>
                </FieldRow>
                {userNewEmail && (
                  <NewEmailCaption>
                    Your current email address is <b>{userEmail}</b> —
                    <ResendLink onClick={handleResendConfirmation}>
                      Resend Confirmation
                    </ResendLink>
                  </NewEmailCaption>
                )}
                <FieldRow>
                  <PasswordField name="password" label="Password">
                    <Input
                      name="password"
                      type="text"
                      value="••••••••"
                      disabled
                    />
                  </PasswordField>

                  <EditPasswordButton
                    onClick={() =>
                      showModal(ChangePasswordModal, {
                        initialEmail: userEmail,
                      })
                    }>
                    Edit&hellip;
                  </EditPasswordButton>
                </FieldRow>
              </>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default SettingsForm
